//libs
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import Helmet from 'react-helmet';

//helpers
import { getPlpJSONLD, getBreadCrumbsData } from 'shared/helpers/meta';
import isEmpty from 'lodash/isEmpty';
import { getMetaInfo } from 'shared/layouts/Listing/components/MetaInfo/helpers';
import { isDynamicPageURL } from 'shared/helpers/utils/url';

//selectors
import {
  getLocation,
  getMetaData,
  getPagenumber,
  getIsOfferPage,
  getFirstTwelveProducts,
  getBreadCrumbs,
} from 'shared/store/listingV2/selectors';
import { getDynamicPageData } from 'shared/store/dynamicPage/selector';

//constants
import { TEXT } from 'shared/layouts/DetailsPage/constants';
import { BRAND_LOGO } from 'shared/constants';

// hook
import { useDynamicPage } from 'shared/routes/DynamicPage/helper';

// context
import { useDomainConfig } from 'shared/domainConfig/context/context';

interface MetaInfo {
  description?: string;
  title?: string;
  keywords?: string[];
  imageUrl?: string;
}

interface MetaProps {
  isSearch: boolean;
}

const Meta = ({ isSearch }: MetaProps) => {
  const metaData = useSelector(getMetaData) as MetaInfo;
  const pageNumber = useSelector(getPagenumber);
  const products = useSelector(getFirstTwelveProducts);
  const { pathname: categoryUrl } = useSelector(getLocation);
  const isOfferPage = useSelector(getIsOfferPage);
  const dynamicPageData = useSelector(getDynamicPageData);
  const breadCrumbs = useSelector(getBreadCrumbs);

  const { slug: dynamicPageSlug } = useDynamicPage();

  const isDpURL = isDynamicPageURL(categoryUrl);
  const showJsonLd = products && (isDpURL || !isSearch);

  const { FAVICON } = useDomainConfig();

  const metaInfo = getMetaInfo({
    metaInfo: metaData,
    pathname: categoryUrl,
    categoryURL: categoryUrl,
    isSearch,
    isOffer: isOfferPage,
    dynamicPageSlug,
    dynamicPageData,
  });

  const breadCrumbsJSON = {
    '@context': 'https://schema.org',
    ...getBreadCrumbsData(breadCrumbs),
  };

  if (isEmpty(metaData) || isEmpty(metaInfo)) return null;

  return (
    <Helmet>
      <title>{metaInfo.metaTitle}</title>
      {metaInfo.metaDescription && <meta name="description" content={metaInfo.metaDescription} />}
      {metaInfo.metaKeywords && <meta name="keywords" content={metaInfo.metaKeywords} />}

      <meta property="og:title" content={metaInfo?.metaTitle} />
      <meta property="og:description" content={metaInfo?.metaDescription} />
      <meta property="og:site_name" content={TEXT.GLOBAL_BRAND_NAME} />
      <meta property="og:image" content={metaData.imageUrl || BRAND_LOGO} />
      <meta name="twitter:title" content={metaInfo?.metaTitle} />
      <meta name="twitter:description" content={metaInfo?.metaDescription} />
      <meta name="twitter:card" content="summary_large_image" />
      {metaInfo.noIndexNoFollowRobotMeta !== '' && (
        <meta name="robots" content={metaInfo.noIndexNoFollowRobotMeta} />
      )}
      {pageNumber !== '1' && <meta name="robots" content="noindex, follow" />}
      <link rel="icon" type="image/x-icon" href={FAVICON} />
      <link rel="shortcut icon" type="image/x-icon" href={FAVICON} />
      {pageNumber === '1' ? <link rel="canonical" href={metaInfo.canonicalUrl} /> : null}

      {showJsonLd && <script type="application/ld+json">{getPlpJSONLD(products)}</script>}
      {breadCrumbs.length > 0 && (
        <script type="application/ld+json">{JSON.stringify(breadCrumbsJSON)}</script>
      )}
    </Helmet>
  );
};

export default memo(Meta);
